/**
 * A named map of keyboard `event.key` string values for common keys.
 *
 * Use these constants instead of raw string literals when comparing against
 * `KeyboardEvent.key` to improve readability and avoid typos. Includes
 * cross-browser aliases for older IE/Edge behaviour (e.g. `KEY_UP_IE: 'Up'`
 * alongside `KEY_UP: 'ArrowUp'`) and covers navigation, function, numpad,
 * modifier, punctuation, and alphanumeric keys.
 *
 * @example
 * ```ts
 * import {keyCodes} from '@pb/kit-util-key-codes';
 *
 * element.addEventListener('keydown', (e) => {
 *   if (e.key === keyCodes.KEY_ENTER) handleSubmit();
 *   if (e.key === keyCodes.KEY_ESCAPE || e.key === keyCodes.KEY_ESC) handleClose();
 * });
 * ```
 */
export const keyCodes = {
    KEY_BACK_SPACE: 'Backspace',
    KEY_TAB: 'Tab',
    KEY_ENTER: 'Enter',
    KEY_SHIFT: 'Shift',
    KEY_CONTROL: 'Control',
    KEY_ALT: 'Alt',
    KEY_CAPS_LOCK: 'CapsLock',
    KEY_ESCAPE: 'Escape',
    KEY_ESC: 'Esc',
    KEY_SPACE: ' ',
    KEY_PAGE_UP: 'PageUp',
    KEY_PAGE_DOWN: 'PageDown',
    KEY_END: 'End',
    KEY_HOME: 'Home',
    KEY_LEFT: 'ArrowLeft',
    KEY_UP: 'ArrowUp',
    KEY_UP_IE: 'Up',
    KEY_RIGHT: 'ArrowRight',
    KEY_DOWN: 'ArrowDown',
    KEY_DOWN_IE: 'Down',
    KEY_PRINTSCREEN: 'PrintScreen',
    KEY_INSERT: 'Insert',
    KEY_DELETE: 'Delete',
    KEY_0: '0',
    KEY_1: '1',
    KEY_2: '2',
    KEY_3: '3',
    KEY_4: '4',
    KEY_5: '5',
    KEY_6: '6',
    KEY_7: '7',
    KEY_8: '8',
    KEY_9: '9',
    KEY_SEMICOLON: ';',
    KEY_EQUALS: '=',
    KEY_A: 'A',
    KEY_B: 'B',
    KEY_C: 'C',
    KEY_D: 'D',
    KEY_E: 'E',
    KEY_F: 'F',
    KEY_G: 'G',
    KEY_H: 'H',
    KEY_I: 'I',
    KEY_J: 'J',
    KEY_K: 'K',
    KEY_L: 'L',
    KEY_M: 'M',
    KEY_N: 'N',
    KEY_O: 'O',
    KEY_P: 'P',
    KEY_Q: 'Q',
    KEY_R: 'R',
    KEY_S: 'S',
    KEY_T: 'T',
    KEY_U: 'U',
    KEY_V: 'V',
    KEY_W: 'W',
    KEY_X: 'X',
    KEY_Y: 'Y',
    KEY_Z: 'Z',
    KEY_CONTEXT_MENU: 'ContextMenu',
    KEY_NUMPAD0: '0',
    KEY_NUMPAD1: '1',
    KEY_NUMPAD2: '2',
    KEY_NUMPAD3: '3',
    KEY_NUMPAD4: '4',
    KEY_NUMPAD5: '5',
    KEY_NUMPAD6: '6',
    KEY_NUMPAD7: '7',
    KEY_NUMPAD8: '8',
    KEY_NUMPAD9: '9',
    KEY_MULTIPLY: '*',
    KEY_ADD: '+',
    KEY_SUBTRACT: '-',
    KEY_SUBTRACT_IE: 'Subtract',
    KEY_DECIMAL: '.',
    KEY_DIVIDE: '/',
    KEY_F1: 'F1',
    KEY_F2: 'F2',
    KEY_F3: 'F3',
    KEY_F4: 'F4',
    KEY_F5: 'F5',
    KEY_F6: 'F6',
    KEY_F7: 'F7',
    KEY_F8: 'F8',
    KEY_F9: 'F9',
    KEY_F10: 'F10',
    KEY_F11: 'F11',
    KEY_F12: 'F12',
    KEY_NUM_LOCK: 'NumLock',
    KEY_SCROLL_LOCK: 'ScrollLock',
    KEY_COMMA: ',',
    KEY_PERIOD: '.',
    KEY_SLASH: '/',
    KEY_BACK_QUOTE: '`',
    KEY_QUOTE: "'",
    KEY_OPEN_BRACKET: '{',
    KEY_CLOSE_BRACKET: '}',
};
